import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { getRegisteredComponent } from 'ca-dom-binding/registerReactComponents';
import { ExportedSingleComponent, PropSetter } from 'ca-dom-binding/types';
import bugsnagReact from '@bugsnag/plugin-react';
import { ThemeProvider } from '@kaizen/design-tokens';
import { themeManager } from 'ca-theme-manager';
import { I18nLoader } from 'ca-ui/i18n';

type RegisterCallback = (arg: PropSetter<any>) => void;

let BugsnagErrorBoundary: any;

function ComponentWrapper({
  registerPropsSetterCallback,
  initialProps,
  Component,
}: {
  registerPropsSetterCallback: RegisterCallback;
  initialProps: any;
  Component: ExportedSingleComponent;
}) {
  const [props, setProps] = useState<any>(initialProps);
  if (!BugsnagErrorBoundary && window.bugsnagClient) {
    window.bugsnagClient.use(bugsnagReact, React);
    BugsnagErrorBoundary = window.bugsnagClient.getPlugin('react');
  }
  const ErrorBoundary = BugsnagErrorBoundary || React.Fragment;
  useEffect(() => {
    registerPropsSetterCallback(setProps);
  }, [setProps, registerPropsSetterCallback]);
  return (
    <ErrorBoundary>
      <ThemeProvider themeManager={themeManager}>
        <I18nLoader>
          <Component {...props} />
        </I18nLoader>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export async function mountReactComponent(
  el: HTMLElement,
  {
    name,
    props,
  }: {
    name: string;
    props: any;
  }
) {
  let setProps: PropSetter<any> | null;
  const registerPropsSetterCallback = (callback: PropSetter<any>) => {
    setProps = callback;
  };
  const Component: ExportedSingleComponent | unknown =
    await getRegisteredComponent(name);

  const root = createRoot(el);
  root.render(
    <ComponentWrapper
      registerPropsSetterCallback={registerPropsSetterCallback}
      initialProps={props}
      Component={Component as ExportedSingleComponent}
    />
  );
  return {
    destroy: root.unmount,
    update: (updatedProps: any) => {
      if (setProps) {
        setProps(updatedProps);
      }
    },
  };
}
